import React, { useState, useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { CalendarIcon, Menu, PlusCircle, Trash2, Info } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/components/ui/use-toast";
import { Label } from "@/components/ui/label";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import type {
  DownloadAgreementResponse,
  EmploymentAgreementFormData,
  GenerateAgreementResponse,
  AgreementDownloadUrlResponse,
} from "@/types/employment-agreement";
import MenuBar from "@/components/MenuBar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";

// Validation schema using zod
const formSchema = z.object({
  employerName: z.string().min(2, "Name must be at least 2 characters"),
  employerAddress: z.string().min(5, "Please enter a valid address"),
  employeeName: z.string().min(2, "Name must be at least 2 characters"),
  employeeAddress: z.string().min(5, "Please enter a valid address"),
  startDate: z.string(),
  probationPeriod: z.number().min(1).max(12),

  childrenDetails: z.array(
    z.object({
      name: z.string().min(1, "Name is required"),
      age: z.number().min(0).max(18),
    }),
  ),

  workAddress: z.string().min(5, "Please enter a valid work address"),
  weeklyHours: z.number().min(1).max(48),
  workStartTime: z.string(),
  workEndTime: z.string(),
  workDays: z.array(z.string()),
  overtimeRate: z.number().min(1),

  salaryAmount: z.number().min(1),
  paymentDate: z.string(),
  paymentMethod: z.enum(["bank_transfer", "other"]),

  annualLeaveWeeks: z.number().min(5.6),
  bankHolidaysIncluded: z.boolean(),
  noticePeriod: z.string(),
  additionalDuties: z.array(
    z.object({
      id: z.string(),
      value: z.string().min(1, "Duty description is required"),
    }),
  ),
});

// Interface for the agreement details response from the API
interface AgreementDetailsResponse {
  status: string;
  agreement: {
    id: number;
    employer_id: number;
    employee_id: number;
  };
  agreementData: EmploymentAgreementFormData;
  error?: string;
}

export default function EmploymentAgreementForm() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { agreementId } = useParams<{ agreementId: string }>();
  const isEditMode = !!agreementId;

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(isEditMode); // Set loading to true if in edit mode

  const form = useForm<EmploymentAgreementFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      probationPeriod: 3,
      weeklyHours: 40,
      overtimeRate: 1.5,
      annualLeaveWeeks: 5.6,
      bankHolidaysIncluded: true,
      childrenDetails: [],
      workDays: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      additionalDuties: [],
      paymentMethod: "bank_transfer",
    },
  });

  const {
    fields: childrenFields,
    append: appendChild,
    remove: removeChild,
  } = useFieldArray<EmploymentAgreementFormData>({
    control: form.control,
    name: "childrenDetails",
  });

  const {
    fields: dutiesFields,
    append: appendDuty,
    remove: removeDuty,
  } = useFieldArray({
    control: form.control,
    name: "additionalDuties" as "additionalDuties", // Use a literal type instead of keyof
  });

  // Load agreement data if in edit mode
  useEffect(() => {
    const fetchAgreementDetails = async () => {
      if (!isEditMode) return;

      try {
        setIsLoading(true);
        const endpoint = EndpointKeys.GET_AGREEMENT_DETAILS;
        const response = await ApiService.request<AgreementDetailsResponse>(
          endpoint,
          {
            method: "GET",
            urlParams: {
              agreement_id: agreementId,
            },
          },
        );

        if (response.status === "success" && response.agreementData) {
          // We need to transform some of the data to match our form structure
          const formattedData = {
            ...response.agreementData,
            // Ensure additionalDuties is in the right format
            additionalDuties: Array.isArray(
              response.agreementData.additionalDuties,
            )
              ? response.agreementData.additionalDuties.map((duty, index) => ({
                  id: index.toString(),
                  value:
                    typeof duty === "string"
                      ? duty
                      : typeof duty === "object" && duty.value
                        ? duty.value
                        : "",
                }))
              : [],
          };

          form.reset(formattedData);
        } else {
          toast({
            title: "Error",
            description: response.error || "Failed to load contract details",
            variant: "destructive",
          });
          navigate("/contracts");
        }
      } catch (error) {
        toast({
          title: "Error",
          description:
            error instanceof Error
              ? error.message
              : "Failed to load contract details",
          variant: "destructive",
        });
        navigate("/contracts");
      } finally {
        setIsLoading(false);
      }
    };

    fetchAgreementDetails();
  }, [agreementId, isEditMode, navigate, toast, form]);

  async function onSubmit(data: EmploymentAgreementFormData) {
    try {
      setIsSubmitting(true);

      if (isEditMode) {
        // Update existing agreement
        const endpoint = EndpointKeys.UPDATE_AGREEMENT;
        const response = await ApiService.request<GenerateAgreementResponse>(
          endpoint,
          {
            method: "PUT",
            body: { agreementData: data },
            urlParams: {
              agreement_id: agreementId,
            },
          },
        );

        if (response.status === "success" && response.agreement_id) {
          toast({
            title: "Success",
            description: "Employment agreement has been updated successfully.",
          });
          navigate("/contracts");
        } else {
          throw new Error(response.error || "Failed to update agreement");
        }
      } else {
        // Create new agreement
        const response = await ApiService.request<GenerateAgreementResponse>(
          EndpointKeys.GENERATE_AGREEMENT,
          {
            method: "POST",
            body: { agreementData: data },
          },
        );

        if (response.status === "success" && response.agreement_id) {
          toast({
            title: "Success",
            description:
              "Employment agreement has been generated successfully.",
          });
          navigate("/contracts");
        } else {
          throw new Error(response.error || "Failed to generate agreement");
        }
      }
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : isEditMode
              ? "Failed to update agreement"
              : "Failed to generate agreement",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen bg-background">
        {/* Mobile Menu Toggle */}
        <div className="md:hidden fixed top-0 left-0 right-0 z-30 flex items-center p-4 bg-gray-900">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="text-white"
          >
            <Menu className="h-6 w-6" />
          </Button>
        </div>

        {/* Sidebar */}
        <div
          className={`${
            isMobileMenuOpen ? "block" : "hidden"
          } md:block fixed md:relative w-64 h-full bg-gray-900 z-20`}
        >
          <MenuBar />
        </div>
        <div className="flex-1 w-full md:w-auto">
          <div className="p-4 md:p-6 max-w-7xl space-y-6 mt-16 md:mt-0">
            <h2 className="text-2xl font-bold">
              {isEditMode
                ? "Edit Employment Contract"
                : "Generate Employment Contract"}
            </h2>
            <div className="space-y-4">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-64 w-full" />
              <Skeleton className="h-64 w-full" />
              <Skeleton className="h-64 w-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      {/* Mobile Menu Toggle */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 flex items-center p-4 bg-gray-900">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="text-white"
        >
          <Menu className="h-6 w-6" />
        </Button>
      </div>

      {/* Sidebar */}
      <div
        className={`${
          isMobileMenuOpen ? "block" : "hidden"
        } md:block fixed md:relative w-64 h-full bg-gray-900 z-20`}
      >
        <MenuBar />
      </div>
      {/* Added margin-left to account for fixed sidebar */}
      <div className="flex-1 w-full md:w-auto">
        <div className="p-4 md:p-6 max-w-7xl space-y-6 mt-16 md:mt-0">
          <h2 className="text-2xl font-bold">
            {isEditMode
              ? "Edit Employment Contract"
              : "Generate Employment Contract"}
          </h2>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
              <Alert className="mb-4">
                <Info className="h-4 w-4 text-primary" />
                <AlertTitle>Employment Contract Management</AlertTitle>
                <AlertDescription>
                  This is where you'll manage your employment contract with your
                  nanny. You should typically complete this once at the
                  beginning of employment, but you can return here to generate a
                  new contract if any terms change. The generated document will
                  be compliant with UK employment regulations and can be signed
                  by both parties. Be sure to fill in all required fields
                  accurately to create a valid and comprehensive agreement.
                </AlertDescription>
              </Alert>

              {/* Employer & Employee Information */}
              <Card>
                <CardHeader>
                  <CardTitle>Basic Information</CardTitle>
                  <CardDescription>
                    Enter the employer and employee details
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="employerName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Employer Name</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="employerAddress"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Employer Address</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="employeeName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Employee Name</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="employeeAddress"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Employee Address</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="startDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Start Date</FormLabel>
                          <FormControl>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button
                                  variant="outline"
                                  className={cn(
                                    "w-full justify-start text-left font-normal",
                                    !field.value && "text-muted-foreground",
                                  )}
                                >
                                  <CalendarIcon className="mr-2 h-4 w-4" />
                                  {field.value ? (
                                    format(
                                      new Date(field.value + "T00:00:00"),
                                      "dd/MM/yyyy",
                                    )
                                  ) : (
                                    <span>Pick a date</span>
                                  )}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0 z-[100]">
                                <Calendar
                                  mode="single"
                                  selected={
                                    field.value
                                      ? new Date(field.value + "T00:00:00")
                                      : undefined
                                  }
                                  defaultMonth={
                                    field.value
                                      ? new Date(field.value)
                                      : undefined
                                  }
                                  onSelect={(date) => {
                                    if (date) {
                                      field.onChange(
                                        date.toISOString().split("T")[0],
                                      );
                                    }
                                  }}
                                  initialFocus
                                />
                              </PopoverContent>
                            </Popover>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="probationPeriod"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Probation Period (months)</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              {...field}
                              onChange={(e) =>
                                field.onChange(parseInt(e.target.value))
                              }
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Children Details */}
              <Card>
                <CardHeader>
                  <CardTitle>Children Details</CardTitle>
                  <CardDescription>
                    Add details for each child under care
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {childrenFields.map((field, index) => (
                      <div key={field.id} className="flex items-end gap-4">
                        <FormField
                          control={form.control}
                          name={`childrenDetails.${index}.name`}
                          render={({ field }) => (
                            <FormItem className="flex-1">
                              <FormLabel>Child's Name</FormLabel>
                              <FormControl>
                                <Input {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={`childrenDetails.${index}.age`}
                          render={({ field }) => (
                            <FormItem className="w-24">
                              <FormLabel>Age</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  {...field}
                                  onChange={(e) =>
                                    field.onChange(parseInt(e.target.value))
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeChild(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => appendChild({ name: "", age: 0 })}
                    >
                      <PlusCircle className="h-4 w-4 mr-2" />
                      Add Child
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Work Schedule */}
              <Card>
                <CardHeader>
                  <CardTitle>Work Schedule</CardTitle>
                  <CardDescription>
                    Define the working hours and location
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <FormField
                    control={form.control}
                    name="workAddress"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Work Address</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <FormField
                      control={form.control}
                      name="weeklyHours"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Weekly Hours</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              {...field}
                              onChange={(e) =>
                                field.onChange(parseInt(e.target.value))
                              }
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="workStartTime"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Start Time</FormLabel>
                          <FormControl>
                            <Input type="time" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="workEndTime"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>End Time</FormLabel>
                          <FormControl>
                            <Input type="time" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Compensation */}
              <Card>
                <CardHeader>
                  <CardTitle>Compensation</CardTitle>
                  <CardDescription>
                    Set salary and payment details
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <FormField
                    control={form.control}
                    name="salaryAmount"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Annual Salary Amount (£)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            {...field}
                            onChange={(e) =>
                              field.onChange(parseFloat(e.target.value))
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="paymentMethod"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Payment Method</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select payment method" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="bank_transfer">
                              Bank Transfer
                            </SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="overtimeRate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Overtime Rate (multiplier)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.1"
                            {...field}
                            onChange={(e) =>
                              field.onChange(parseFloat(e.target.value))
                            }
                          />
                        </FormControl>
                        <FormDescription>
                          Standard overtime rate is 1.5 times the regular pay
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="paymentDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Payment Date</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select payment date" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="last working day">
                              Last Working Day
                            </SelectItem>
                            <SelectItem value="first working day">
                              First Working Day
                            </SelectItem>
                            <SelectItem value="25th">25th of Month</SelectItem>
                            <SelectItem value="friday">Every Friday</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* Leave and Benefits */}
              <Card>
                <CardHeader>
                  <CardTitle>Leave and Benefits</CardTitle>
                  <CardDescription>
                    Configure leave entitlements and notice periods
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <FormField
                    control={form.control}
                    name="annualLeaveWeeks"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Annual Leave (weeks)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.1"
                            {...field}
                            onChange={(e) =>
                              field.onChange(parseFloat(e.target.value))
                            }
                          />
                        </FormControl>
                        <FormDescription>
                          Minimum statutory leave is 5.6 weeks per year
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="bankHolidaysIncluded"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <FormLabel className="text-base">
                            Bank Holidays Included
                          </FormLabel>
                          <FormDescription>
                            Are bank holidays included in the annual leave
                            allowance?
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="noticePeriod"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Notice Period</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select notice period" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="1_month">1 Month</SelectItem>
                            <SelectItem value="2_months">2 Months</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* Additional Duties */}
              <Card>
                <CardHeader>
                  <CardTitle>Additional Duties</CardTitle>
                  <CardDescription>
                    Specify any additional responsibilities
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {dutiesFields.map((field, index) => (
                      <div key={field.id} className="flex items-center gap-4">
                        <FormField
                          control={form.control}
                          name={`additionalDuties.${index}.value`}
                          render={({ field }) => (
                            <FormItem className="flex-1">
                              <FormControl>
                                <Input
                                  {...field}
                                  placeholder="Enter additional duty"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeDuty(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}

                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        appendDuty({
                          id: dutiesFields.length.toString(),
                          value: "",
                        }) as any; // Type assertion to handle complex types
                      }}
                    >
                      <PlusCircle className="h-4 w-4 mr-2" />
                      Add Duty
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Form Actions */}
              <div className="flex justify-end gap-4 px-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/contracts")}
                  disabled={isSubmitting}
                  className="w-full md:w-auto"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full md:w-auto"
                >
                  {isSubmitting
                    ? isEditMode
                      ? "Updating..."
                      : "Generating..."
                    : isEditMode
                      ? "Update Agreement"
                      : "Generate Agreement"}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </div>
  );
}
